import {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    ArrowRight,
    Waves,
    Trophy,
    Dumbbell,
    Gamepad2,
    Cog,
    Sparkles,
    Handshake,
    Maximize2,
    X,
    ZoomIn,
    ZoomOut,
} from 'lucide-react';

import {
    ATTRACTION_CATEGORY_LABELS,
    ROUTES,
    buildCompanyWhatsAppUrl,
} from '@/lib/data';
import { publicApi } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { fadeUp, stagger } from '@/lib/motion';

const CATEGORY_META = {
    acuaticos: {
        icon: Waves,
        ring: 'ring-[hsl(var(--kche-cyan))]',
        accent:
            'bg-[hsl(var(--kche-cyan))] text-[hsl(var(--kche-dark))]',
    },
    competencia: {
        icon: Trophy,
        ring: 'ring-[hsl(var(--kche-yellow))]',
        accent:
            'bg-[hsl(var(--kche-yellow))] text-[hsl(var(--kche-dark))]',
    },
    deportivos: {
        icon: Dumbbell,
        ring: 'ring-[hsl(var(--kche-green))]',
        accent:
            'bg-[hsl(var(--kche-green))] text-white',
    },
    interactivos: {
        icon: Gamepad2,
        ring: 'ring-[hsl(var(--kche-blue))]',
        accent:
            'bg-[hsl(var(--kche-blue))] text-white',
    },
    mecanicos: {
        icon: Cog,
        ring: 'ring-[hsl(var(--kche-purple))]',
        accent:
            'bg-[hsl(var(--kche-purple))] text-white',
    },
    otros: {
        icon: Sparkles,
        ring: 'ring-[hsl(var(--kche-yellow))]',
        accent:
            'bg-[hsl(var(--kche-yellow))] text-[hsl(var(--kche-dark))]',
    },
};

const AttractionImageLightbox = ({
    item,
    onClose,
}) => {
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow = 'hidden';

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }

            if (
                event.key === '+' ||
                event.key === '='
            ) {
                setZoom((current) =>
                    Math.min(current + 0.25, 2.5)
                );
            }

            if (event.key === '-') {
                setZoom((current) =>
                    Math.max(current - 0.25, 1)
                );
            }
        };

        window.addEventListener(
            'keydown',
            onKeyDown
        );

        return () => {
            document.body.style.overflow =
                previousOverflow;

            window.removeEventListener(
                'keydown',
                onKeyDown
            );
        };
    }, [onClose]);

    if (
        typeof document === 'undefined' ||
        !item?.image_url
    ) {
        return null;
    }

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label={`Imagen ampliada de ${item.title}`}
            onMouseDown={(event) => {
                if (
                    event.target ===
                    event.currentTarget
                ) {
                    onClose();
                }
            }}
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 z-30 inline-flex size-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 transition"
                aria-label="Cerrar imagen"
            >
                <X className="size-6" />
            </button>

            <div className="absolute left-4 top-4 z-30 max-w-[calc(100%-140px)] rounded-full bg-black/50 px-4 py-2 text-sm font-bold text-white backdrop-blur">
                {item.title}
            </div>

            <motion.div
                initial={{
                    opacity: 0,
                    scale: 0.96,
                }}
                animate={{
                    opacity: 1,
                    scale: 1,
                }}
                exit={{
                    opacity: 0,
                    scale: 0.96,
                }}
                transition={{ duration: 0.22 }}
                className="flex h-[82vh] w-full max-w-6xl items-center justify-center overflow-auto rounded-2xl"
            >
                <img
                    src={item.image_url}
                    alt={item.title || "Atracción K'che"}
                    draggable="false"
                    onDoubleClick={() => {
                        setZoom((current) =>
                            current === 1 ? 1.75 : 1
                        );
                    }}
                    className="max-h-full max-w-full select-none object-contain transition-transform duration-300 ease-out"
                    style={{
                        transform: `scale(${zoom})`,
                    }}
                />
            </motion.div>

            <div className="absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/60 p-2 text-white backdrop-blur">
                <button
                    type="button"
                    onClick={() =>
                        setZoom((current) =>
                            Math.max(
                                current - 0.25,
                                1
                            )
                        )
                    }
                    disabled={zoom <= 1}
                    className="inline-flex size-10 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-35 transition"
                    aria-label="Alejar imagen"
                >
                    <ZoomOut className="size-5" />
                </button>

                <span className="min-w-14 text-center text-sm font-bold">
                    {Math.round(zoom * 100)}%
                </span>

                <button
                    type="button"
                    onClick={() =>
                        setZoom((current) =>
                            Math.min(
                                current + 0.25,
                                2.5
                            )
                        )
                    }
                    disabled={zoom >= 2.5}
                    className="inline-flex size-10 items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-35 transition"
                    aria-label="Acercar imagen"
                >
                    <ZoomIn className="size-5" />
                </button>
            </div>
        </motion.div>,
        document.body
    );
};

export const AttractionCard = ({ item }) => {
    const [imageOpen, setImageOpen] =
        useState(false);

    const meta =
        CATEGORY_META[item.category] ||
        CATEGORY_META.otros;

    const Icon = meta.icon;

    const attractionName =
        item.title || 'esta atracción';

    const playMessage =
        `Hola, quiero información para ir a jugar en la atracción ${attractionName}.`;

    const rentMessage =
        `Hola, quiero información para alquilar la atracción ${attractionName}.`;

    const playUrl =
        buildCompanyWhatsAppUrl(playMessage);

    const rentUrl =
        buildCompanyWhatsAppUrl(rentMessage);

    const closeImage = useCallback(() => {
        setImageOpen(false);
    }, []);

    const openImage = () => {
        if (!item.image_url) return;

        setImageOpen(true);

        trackEvent(
            'attraction_image_open',
            window.location.pathname,
            {
                id: item.id,
                title: item.title,
                category: item.category,
            }
        );
    };

    const trackWhatsApp = (intent) => {
        trackEvent(
            'whatsapp_click',
            window.location.pathname,
            {
                from: 'attraction_card',
                intent,
                attraction_id: item.id,
                attraction_name: item.title,
                category: item.category,
            }
        );
    };

    return (
        <>
            <motion.div
                variants={fadeUp}
                data-testid="attraction-card"
                className="group rounded-3xl bg-white border border-border overflow-hidden flex flex-col shadow-soft hover:shadow-pop hover:-translate-y-1 transition-all duration-300"
            >
                <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
                    {item.image_url ? (
                        <button
                            type="button"
                            onClick={openImage}
                            className="absolute inset-0 block h-full w-full overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--kche-pink))]"
                            aria-label={`Ampliar imagen de ${attractionName}`}
                        >
                            <img
                                src={item.image_url}
                                alt={attractionName}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />

                            <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                            <span className="absolute right-3 top-3 inline-flex size-10 items-center justify-center rounded-full bg-white/90 text-[hsl(var(--kche-dark))] opacity-0 scale-90 shadow-soft transition-all group-hover:opacity-100 group-hover:scale-100">
                                <Maximize2 className="size-4" />
                            </span>
                        </button>
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            Sin imagen
                        </div>
                    )}

                    <span
                        className={`absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold font-display ${meta.accent} shadow-soft pointer-events-none`}
                    >
                        <Icon className="size-3.5" />

                        {ATTRACTION_CATEGORY_LABELS[
                            item.category
                        ] || item.category}
                    </span>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-2xl font-display font-bold">
                        {item.title}
                    </h3>

                    <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                        {item.description}
                    </p>

                    {item.bullets?.length > 0 ? (
                        <ul className="mt-3 space-y-1 text-sm text-foreground/80">
                            {item.bullets
                                .slice(0, 3)
                                .map((bullet, index) => (
                                    <li
                                        key={index}
                                        className="flex items-center gap-2"
                                    >
                                        <span className="size-1.5 shrink-0 rounded-full bg-[hsl(var(--kche-pink))]" />
                                        {bullet}
                                    </li>
                                ))}
                        </ul>
                    ) : null}

                    <div className="mt-auto pt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <a
                            href={playUrl || '#'}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="attraction-card-play"
                            onClick={(event) => {
                                if (!playUrl) {
                                    event.preventDefault();
                                    return;
                                }

                                trackWhatsApp('play');
                            }}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[hsl(var(--kche-pink))] to-[hsl(var(--kche-purple))] px-4 py-3 text-center text-sm font-bold font-display text-white shadow-pop hover:scale-[1.025] active:scale-[0.98] transition-transform"
                        >
                            <Gamepad2 className="size-4 shrink-0" />
                            Ven a jugar
                        </a>

                        <a
                            href={rentUrl || '#'}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="attraction-card-rent"
                            onClick={(event) => {
                                if (!rentUrl) {
                                    event.preventDefault();
                                    return;
                                }

                                trackWhatsApp('rent');
                            }}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[hsl(var(--kche-cyan))] to-[hsl(var(--kche-blue))] px-4 py-3 text-center text-sm font-bold font-display text-white shadow-soft hover:scale-[1.025] active:scale-[0.98] transition-transform"
                        >
                            <Handshake className="size-4 shrink-0" />
                            Ven a alquilar
                        </a>
                    </div>
                </div>
            </motion.div>

            <AnimatePresence>
                {imageOpen ? (
                    <AttractionImageLightbox
                        item={item}
                        onClose={closeImage}
                    />
                ) : null}
            </AnimatePresence>
        </>
    );
};

export const AttractionsPreview = ({
    limit = 3,
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] =
        useState(true);

    useEffect(() => {
        publicApi
            .getAttractions()
            .then((data) => {
                setItems(
                    Array.isArray(data)
                        ? data
                        : []
                );
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const visible = items.slice(0, limit);

    return (
        <section
            data-testid="home-attractions-preview"
            className="relative bg-background py-16 sm:py-20 lg:py-24"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-sm font-bold font-display text-[hsl(var(--kche-pink))] uppercase tracking-wider">
                            Atracciones
                        </p>

                        <h2 className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-display font-bold">
                            Vive una{' '}
                            <span className="text-gradient-fun">
                                aventura
                            </span>{' '}
                            única
                        </h2>
                    </div>

                    <Link
                        to={ROUTES.atracciones}
                        className="inline-flex items-center gap-2 text-sm font-bold font-display text-[hsl(var(--kche-pink))] hover:text-[hsl(var(--kche-purple))]"
                        data-testid="home-attractions-see-all"
                    >
                        Ver todas
                        <ArrowRight className="size-4" />
                    </Link>
                </div>

                {loading ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {[0, 1, 2].map((index) => (
                            <div
                                key={index}
                                className="animate-pulse rounded-3xl bg-secondary aspect-[5/4]"
                            />
                        ))}
                    </div>
                ) : (
                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{
                            once: true,
                            amount: 0.15,
                        }}
                        variants={stagger}
                        className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
                    >
                        {visible.map((item) => (
                            <AttractionCard
                                key={item.id}
                                item={item}
                            />
                        ))}
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default AttractionsPreview;